import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "../../api/axios";
import { buildEnclosureFromSettings } from "../../builders/EnclosureInitializer";
import { buildSectionPlacements } from "../../builders/SectionPlacementBuilder";
import { buildSubGroupPlacements } from "../../builders/SubGroupPlacementBuilder";
import { saveFullEnclosurePlacement } from "../../builders/EnclosurePlacementSaver";
import { ORIENTATION, normalizeOrientation, normalizeRail, placementEndpoint } from "../../builders/layoutMode";
import { createLayoutChannel } from "./layoutChannel";

const LayoutContext = createContext(null);
export const useLayout = () => useContext(LayoutContext);

// Fixed id for the cabinet/enclosure row in the tree — every other row uses
// device.subGroup.id, which is also what deviceNodeMap (3D) and the
// devices prop (2D) are keyed/filterable by.
export const ENCLOSURE_ID = "__enclosure__";

// Selecting a whole section reuses the single shared selectedId: the id of a
// section selection is "section:<sectionPlacement.id>". Every view resolves it
// to the SubGroup ids of that section with selectionIds() below, so no extra
// props / state are needed anywhere.
export const SECTION_PREFIX = "section:";
export const sectionSelectionId = (sectionKey) => `${SECTION_PREFIX}${sectionKey}`;
export const isSectionSelection = (id) => typeof id === "string" && id.startsWith(SECTION_PREFIX);

// selectedId -> Set of SubGroup ids that are selected
// (one device, or every device of the selected section).
export function selectionIds(selectedId, sectionPlacements) {
  if (!selectedId) return new Set();
  if (!isSectionSelection(selectedId)) return new Set([selectedId]);

  const key = selectedId.slice(SECTION_PREFIX.length);
  const sp = (sectionPlacements ?? []).find((p) => String(p.id) === key);
  const entries = sp?.subGroupPlacements ?? sp?.SubGroupPlacements ?? [];
  return new Set(entries.map((e) => e.referenceId ?? e.ReferenceId).filter(Boolean));
}

export function LayoutProvider({ projectId, runEngine = true, children }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [selectedScenario, setSelectedScenario] = useState(null);

  const [enclosureTooSmall, setEnclosureTooSmall] = useState(false);
  const [requiredHeight, setRequiredHeight] = useState(null);

  const [canvasSnapshot, setCanvasSnapshot] = useState(null);
  const [canvasVersion, setCanvasVersion] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isCanvasLoading, setIsCanvasLoading] = useState(false);
  const [isManualCalculating, setIsManualCalculating] = useState(false);

  // true after an enclosure/scenario change until "Recalculate layout" has run
  const [layoutOutdated, setLayoutOutdated] = useState(false);
  const calculatingRef = useRef(false);

  const [climate, setClimateState] = useState({
    altitude: 50, minInner: 10, maxInner: 35, minOuter: 0, maxOuter: 40, voltage: 230, frequency: 50,
  });
  const [climateResult, setClimateResult] = useState(null);

  // ── Shared hide/select state — view-agnostic (works for 2D and 3D) ─────
  // Keyed by device.subGroup.id (ENCLOSURE_ID for the cabinet). This is
  // real React state, not a mutated object, so both canvases AND the tree
  // re-render automatically whenever it changes — no forced re-renders
  // needed anywhere anymore.
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  const [selectedId, setSelectedId] = useState(null);

  // Latest values for the sync code (updated immediately on local changes,
  // so two fast clicks before a re-render still compute from the right state)
  const hiddenRef = useRef(hiddenIds);
  const selectedRef = useRef(selectedId);
  hiddenRef.current = hiddenIds;
  selectedRef.current = selectedId;

  // Data only now (no callbacks) — reported by ViewerPanel once
  // PanelCanvasReal3D has built its scene: { cabinetNodes, deviceNodeMap,
  // onFit }. Only PanelCanvasReal3D needs these; the tree and PanelCanvas2D
  // work off hiddenIds/selectedId above instead.
  const [sceneHandlers, setSceneHandlers] = useState(null);

  const enclosurePlacement = selectedProject?.enclosurePlacement ?? null;
  const hasEnclosure = !!enclosurePlacement;
  const devices = enclosurePlacement?.devices ?? [];
  const sectionPlacements = enclosurePlacement?.sectionPlacements ?? [];

  // ── Orientation ─────────────────────────────────────────────────────────
  // The backend may return "SpaceOriented" or 1 (the settings panel saves
  // numbers), so always go through normalizeOrientation.
  const orientation = useMemo(
    () => normalizeOrientation(selectedScenario?.settings?.orientation),
    [selectedScenario?.settings?.orientation],
  );
  const useOptimized = orientation === ORIENTATION.SPACE;     // unchanged meaning
  const useFlexible = orientation === ORIENTATION.FLEXIBLE;

  // ───────────── messaging ─────────────
  const chRef = useRef(null);
  const handlersRef = useRef({});
  const broadcast = useCallback((msg) => chRef.current?.send(msg), []);

  // Marks the geometry as outdated (true) or up to date (false) in all panels
  const setOutdated = useCallback((value) => {
    setLayoutOutdated(value);
    broadcast({ type: "layout-outdated", value });
  }, [broadcast]);

  // Hide/select are broadcast ONLY on local user actions, never from an
  // effect: a state that arrived from another panel must not be sent back,
  // otherwise the panels echo it to each other endlessly and it flickers.
  const toggleHidden = useCallback((id) => {
    if (!id) return;
    const next = new Set(hiddenRef.current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    hiddenRef.current = next;
    setHiddenIds(next);
    broadcast({ type: "hidden-ids", ids: [...next] });
  }, [broadcast]);

  // Hide (true) or show (false) many ids at once, e.g. a whole section in the
  // tree: one state update and one broadcast instead of one per device.
  const setHiddenMany = useCallback((ids, hidden) => {
    const list = (ids ?? []).filter(Boolean);
    if (!list.length) return;
    const next = new Set(hiddenRef.current);
    list.forEach((id) => (hidden ? next.add(id) : next.delete(id)));
    hiddenRef.current = next;
    setHiddenIds(next);
    broadcast({ type: "hidden-ids", ids: [...next] });
  }, [broadcast]);

  // Click the same id again to deselect; passing null always clears,
  // regardless of what's currently selected (used for "click empty space").
  const selectId = useCallback((id) => {
    const next = selectedRef.current === id ? null : id;
    selectedRef.current = next;
    setSelectedId(next);
    broadcast({ type: "selected-id", id: next });
  }, [broadcast]);

  useEffect(() => {
    if (!runEngine) return;
    if (!selectedProject?.id) return;
    if (!selectedScenario) return;

    runAutoLayoutPipeline(selectedProject, selectedScenario);
  }, [projectId, selectedScenario?.id]);

  useEffect(() => {
    if (!projectId) return;
    const ch = createLayoutChannel(projectId, (m) => handlersRef.current.onMessage?.(m));
    chRef.current = ch;
    // A panel that (re)loads, e.g. the right iframe after a button click,
    // asks the others for the current hide/select state instead of
    // overwriting it with its own empty state.
    ch.send({ type: "state-request" });
    return () => { ch.close(); chRef.current = null; };
  }, [projectId]);

  // Hard cap: the manual spinner can never hang
  useEffect(() => {
    if (!isManualCalculating) return;
    const id = setTimeout(() => {
      console.warn("Manual layout spinner timed out; forcing it off");
      endManual();
    }, 20000);
    return () => clearTimeout(id);
  }, [isManualCalculating]);

  // ───────────── project ─────────────
  const refetchProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const fresh = await api.get(`/project/${projectId}`).then((r) => r.data);
      setSelectedProject(fresh);
    } catch (err) {
      console.error("Failed to load project:", err);
    }
  }, [projectId]);

  useEffect(() => { refetchProject(); }, [refetchProject]);
  useEffect(() => { setProjectName(selectedProject?.name ?? ""); }, [selectedProject?.id]);

  const refreshProject = useCallback(async () => {
    await refetchProject();
    broadcast({ type: "project-changed" });
  }, [refetchProject, broadcast]);

  const updateProject = useCallback((p) => {
    setSelectedProject(p);
    broadcast({ type: "project-changed" });
  }, [broadcast]);

  const handleNameChange = async (newName) => {
    setProjectName(newName);
    if (!selectedProject?.id) return;
    const updated = { ...selectedProject, name: newName };
    await api.put(`/project/${selectedProject.id}`, updated);
    updateProject(updated);
    window.dispatchEvent(new Event("project-updated"));
  };

  // ───────────── 2D canvas capture (layout report) ─────────────
  // PanelCanvas2D registers a function returning { image, snapshot } (the
  // picture and the layout exactly as drawn). The canvas may live in another
  // panel / iframe, so a request falls back to the layout channel.
  const canvasCaptureRef = useRef(null);
  const pendingCapturesRef = useRef(new Map());

  const registerCanvasCapture = useCallback((fn) => {
    canvasCaptureRef.current = fn;
    return () => {
      if (canvasCaptureRef.current === fn) canvasCaptureRef.current = null;
    };
  }, []);

  // Let React / Konva draw pending state changes (e.g. cleared selection) first
  const runLocalCapture = useCallback(async () => {
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      return canvasCaptureRef.current?.() ?? null;
    } catch (err) {
      console.error("Canvas capture failed:", err);
      return null;
    }
  }, []);

  // Resolves to { image: { dataUrl, width, height }, snapshot } or null (no 2D canvas open)
  const requestCanvasCapture = useCallback(({ timeoutMs = 5000 } = {}) => {
    if (canvasCaptureRef.current) return runLocalCapture();

    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        pendingCapturesRef.current.delete(requestId);
        resolve(null);
      }, timeoutMs);
      pendingCapturesRef.current.set(requestId, (result) => {
        clearTimeout(timer);
        resolve(result ?? null);
      });
      broadcast({ type: "canvas-capture-request", requestId });
    });
  }, [broadcast, runLocalCapture]);

  // ───────────── configurator links ─────────────
  // The device's subGroup (and the enclosure) in the project is a copy of the
  // article taken when it was added, so it doesn't know links set later.
  // The current articles are the source: device.subGroup.id is the SubGroup
  // article id, enclosurePlacement.referenceId the enclosure article id.
  const [configuratorLinks, setConfiguratorLinks] = useState(() => ({
    subGroups: new Map(),
    enclosures: new Map(),
    enclosureList: [],
  }));

  const loadConfiguratorLinks = useCallback(async () => {
    // Accepts a plain array or a wrapped/paged response ({ items }, { data }, ...)
    const unwrap = (d) =>
      Array.isArray(d) ? d : d?.items ?? d?.data ?? d?.results ?? d?.value ?? [];
    const list = (url) => api.get(url).then((r) => unwrap(r.data)).catch((err) => {
      console.warn(`Configurator links: failed to load ${url}`, err);
      return [];
    });
    const toMap = (articles) => new Map(
      (Array.isArray(articles) ? articles : [])
        .filter((a) => a?.id && a.configuratorUrl)
        .map((a) => [String(a.id), a.configuratorUrl]),
    );

    const [subGroups, enclosures] = await Promise.all([list("/subgroup"), list("/enclosure")]);
    setConfiguratorLinks({
      subGroups: toMap(subGroups),
      enclosures: toMap(enclosures),
      enclosureList: Array.isArray(enclosures) ? enclosures : [],
    });
  }, []);

  // On project load, and when the user comes back to this tab (the link may
  // have been edited in the article library meanwhile).
  useEffect(() => {
    if (!projectId) return;
    loadConfiguratorLinks();
    window.addEventListener("focus", loadConfiguratorLinks);
    return () => window.removeEventListener("focus", loadConfiguratorLinks);
  }, [projectId, loadConfiguratorLinks]);

  const getConfiguratorUrl = useCallback(
    (device) =>
      configuratorLinks.subGroups.get(String(device?.subGroup?.id)) ??
      device?.subGroup?.configuratorUrl ??
      null,
    [configuratorLinks],
  );

  const enclosureConfiguratorUrl = useMemo(() => {
    const ep = enclosurePlacement;
    if (!ep) return null;

    // 1) By id: whichever field of the placement holds the article id
    const ids = [ep.referenceId, ep.ReferenceId, ep.articleId, ep.enclosureId, ep.sourceArticleId]
      .filter(Boolean)
      .map(String);
    for (const id of ids) {
      const url = configuratorLinks.enclosures.get(id);
      if (url) return url;
    }

    // 2) By article number, only if exactly one enclosure article matches
    //    (the same number can exist for several manufacturers / variants)
    const number = ep.articleNumber ?? ep.partNumber ?? ep.enclosure?.articleNumber;
    if (number) {
      const matches = configuratorLinks.enclosureList.filter(
        (a) => a?.articleNumber === number && a.configuratorUrl,
      );
      if (matches.length === 1) return matches[0].configuratorUrl;
    }

    // 3) A link stored on the placement itself
    return ep.configuratorUrl ?? null;
  }, [configuratorLinks, enclosurePlacement]);

  // ───────────── scenario ─────────────
  useEffect(() => {
    if (!selectedProject?.scenarioId) { setSelectedScenario(null); return; }
    api.get(`/scenario/${selectedProject.scenarioId}`)
      .then((r) => setSelectedScenario(r.data))
      .catch(() => setSelectedScenario(null));
  }, [selectedProject?.scenarioId]);

  // Saving the scenario no longer calculates anything: it only marks the layout as outdated
  const handleScenarioSelected = async (scenario) => {
    if (!selectedProject?.id || !scenario) return;
    await api.put(`/project/${selectedProject.id}`, { ...selectedProject, scenarioId: scenario.id });
    await refreshProject();
    setOutdated(true);
  };

  // ───────────── devices ─────────────
  const saveDevice = async (id, patch) => {
    if (!selectedProject?.enclosurePlacement) return;
    const target = devices.find((d) => d.id === id);
    if (!target) return;
    await api.put(`/projects/${selectedProject.id}/enclosure/devices/${id}`, { ...target, ...patch });
    await refreshProject();
  };
  const handleQuantityChange = useCallback((id, quantity) => saveDevice(id, { quantity }), [selectedProject, devices]);
  const handlePlaceholderQuantityChange = useCallback((id, placeholderQuantity) => saveDevice(id, { placeholderQuantity }), [selectedProject, devices]);
  // Flexible Oriented: rail direction per device (= per SubGroup / tree row),
  // RAIL.HORIZONTAL / VERTICAL / AUTO. Stored on the project's device, so it
  // survives the rebuild of the section/subgroup placements; the geometry is
  // only recalculated by the button.
  const handleRailOrientationChange = useCallback(async (id, railOrientation) => {
    await saveDevice(id, { railOrientation });
    setOutdated(true);
  }, [selectedProject, devices, setOutdated]);

  // Flexible Oriented: units per vertical rail for one device (tree field next
  // to Reserve). null = use the scenario value, 0 = no limit.
  const handleUnitsPerRailChange = useCallback(async (id, maxUnitsPerRail) => {
    await saveDevice(id, { maxUnitsPerRail });
    setOutdated(true);
  }, [selectedProject, devices, setOutdated]);

  // Flexible Oriented: one rail setting for many devices (a whole section).
  // Saved one after another (the device endpoint may rewrite the whole
  // enclosure document, so parallel writes could overwrite each other),
  // then the project is reloaded once.
  const handleRailOrientationChangeMany = useCallback(async (deviceIds, railOrientation) => {
    const project = selectedProject;
    if (!project?.enclosurePlacement || !deviceIds?.length) return;

    const wanted = normalizeRail(railOrientation);
    const ids = new Set(deviceIds);
    const targets = devices.filter((d) => ids.has(d.id) && normalizeRail(d.railOrientation) !== wanted);
    if (!targets.length) return;

    // Show the new state immediately
    const changed = new Set(targets.map((d) => d.id));
    setSelectedProject((prev) => {
      if (!prev?.enclosurePlacement) return prev;
      return {
        ...prev,
        enclosurePlacement: {
          ...prev.enclosurePlacement,
          devices: (prev.enclosurePlacement.devices ?? []).map((d) =>
            changed.has(d.id) ? { ...d, railOrientation: wanted } : d),
        },
      };
    });

    try {
      for (const d of targets) {
        await api.put(`/projects/${project.id}/enclosure/devices/${d.id}`, { ...d, railOrientation: wanted });
      }
    } catch (err) {
      console.error("Failed to save rail orientation:", err);
    }
    await refreshProject();   // server state wins (also undoes the local change on failure)
    setOutdated(true);
  }, [selectedProject, devices, refreshProject, setOutdated]);

  // Flexible Oriented: manual device order inside one section (drag & drop in
  // the tree). orderedDeviceIds = all device ids of that section, in the new
  // order. Applied locally first so the tree doesn't jump back while saving.
  const handleReorderDevices = useCallback(async (orderedDeviceIds) => {
    if (!selectedProject?.enclosurePlacement || !orderedDeviceIds?.length) return;

    const position = new Map(orderedDeviceIds.map((id, i) => [id, i]));
    setSelectedProject((prev) => {
      if (!prev?.enclosurePlacement) return prev;
      return {
        ...prev,
        enclosurePlacement: {
          ...prev.enclosurePlacement,
          devices: (prev.enclosurePlacement.devices ?? []).map((d) =>
            position.has(d.id) ? { ...d, sortOrder: position.get(d.id) } : d),
        },
      };
    });

    try {
      await api.put(`/projects/${selectedProject.id}/enclosure/devices/order`, orderedDeviceIds);
    } catch (err) {
      console.error("Failed to save device order:", err);
    }
    await refreshProject();   // server state wins (also undoes the local order on failure)
    setOutdated(true);
  }, [selectedProject, refreshProject, setOutdated]);

  const handleDeleteDevice = useCallback(async (deviceId) => {
    if (!selectedProject?.enclosurePlacement) return;
    await api.delete(`/projects/${selectedProject.id}/enclosure/devices/${deviceId}`);
    await refreshProject();
  }, [selectedProject]);

  function clearEnclosureBlock() {
    setEnclosureTooSmall(false);
    setRequiredHeight(null);
  }

  // ───────────── engine (runEngine only) ─────────────
  const manualPendingRef = useRef(false);
  const sawSceneLoadingRef = useRef(false);
  const viewModeRef = useRef("2D");
  const reportViewMode = useCallback((m) => { viewModeRef.current = m; }, []);

  const endManual = () => {
    manualPendingRef.current = false;
    sawSceneLoadingRef.current = false;
    setIsManualCalculating(false);
  };

  // Builders + save + backend placement. Only ever started by the
  // "Recalculate layout" button (no automatic triggers anymore).
  async function runAutoLayoutPipeline(project, scenario, manual = false) {
    if (!project?.enclosurePlacement || !scenario) {
      if (manual) endManual();
      return;
    }
    try {
      // The scenario in state is only reloaded when the project switches to a
      // different scenario. Settings / sections edited in the scenario panel
      // (orientation, flexible options, rail orientation per section) would
      // otherwise be ignored here, so always work with a fresh copy.
      const freshScenario = await api.get(`/scenario/${scenario.id}`).then((r) => r.data);
      if (freshScenario) {
        scenario = freshScenario;
        setSelectedScenario(freshScenario);
      }

      const subGroups = await api.get("/subgroup").then((r) => r.data);
      const latestProject = await api.get(`/project/${project.id}`).then((r) => r.data);
      if (latestProject == null) {
        if (manual) endManual();
        return;
      }

      let enclosure = await buildEnclosureFromSettings(project.id, latestProject.enclosurePlacement, scenario.settings);
      let sps = await buildSectionPlacements(scenario, enclosure);
      let subGroupPlacements = await buildSubGroupPlacements(scenario, sps, subGroups);
      sps.forEach((sp) => { sp.subGroupPlacements = subGroupPlacements.filter((sg) => sg.parentId === sp.id); });
      enclosure.sectionPlacements = sps;

      await saveFullEnclosurePlacement(project.id, enclosure);

      // Use the freshly saved project (with ducts), not this render's stale selectedProject
      const fresh = await api.get(`/project/${project.id}`).then((r) => r.data);
      setSelectedProject(fresh);
      broadcast({ type: "project-changed" });
      await refreshLayout(manual, fresh, scenario);
      // refreshLayout already bumps canvasVersion
    } catch (err) {
      console.error("Layout pipeline failed:", err);
      if (manual) endManual();
    }
  }

  async function refreshLayout(manual = false, projectArg = null, scenarioArg = null) {
    const project = projectArg ?? selectedProject;
    const scenario = scenarioArg ?? selectedScenario;
    if (!project?.id || !project?.enclosurePlacement) {
      if (manual) endManual();
      return;
    }
    if (calculatingRef.current) {
      if (manual) endManual();
      return;
    }
    calculatingRef.current = true;
    setIsCalculating(true);
    let skipCanvasWait = false; // true = no 3D rebuild to wait for
    const pid = project.id;
    const enclosure = project.enclosurePlacement;
    const montagePlate = enclosure.montagePlacement;
    try {
      // Endpoint from the scenario passed in (fresh), not from this render's state
      const endpoint = placementEndpoint(pid, scenario?.settings?.orientation);
      const res = await api.post(endpoint);
      const { enclosureHeight, generatedSections } = res.data;
      const tooSmall = enclosureHeight > montagePlate.size.height;
      skipCanvasWait = tooSmall; // the alert replaces the canvas, no loading events
      setRequiredHeight(enclosureHeight);
      setEnclosureTooSmall(tooSmall);
      setCanvasSnapshot({
        cabinet: enclosure,
        montagePlate: enclosure.montagePlacement,
        devices: enclosure.devices ?? [],
        ductLeft: enclosure.ductLeft,
        ductRight: enclosure.ductRight,
        ductTopEnclosure: enclosure.ductTop,
        generatedSections: generatedSections ?? [],
      });
      setCanvasVersion((v) => v + 1);
      setOutdated(false); // geometry is up to date again
    } catch (err) {
      console.error("Layout calculation failed:", err);
      skipCanvasWait = true;
    } finally {
      calculatingRef.current = false;
      setIsCalculating(false);
      if (manual && (skipCanvasWait || viewModeRef.current !== "3DReal")) {
        endManual();
      } else if (manual) {
        // 3D view: if no scene rebuild starts shortly after, don't keep waiting
        setTimeout(() => {
          if (manualPendingRef.current && !sawSceneLoadingRef.current) endManual();
        }, 1000);
      }
    }
  }

  const runManualLayout = () => {
    if (manualPendingRef.current) return; // already running
    manualPendingRef.current = true;
    sawSceneLoadingRef.current = isCanvasLoading;
    setIsManualCalculating(true);
    runAutoLayoutPipeline(selectedProject, selectedScenario, true);
  };

  useEffect(() => {
    if (!manualPendingRef.current) return;
    if (isCanvasLoading) sawSceneLoadingRef.current = true;
    else if (sawSceneLoadingRef.current) {
      manualPendingRef.current = false;
      sawSceneLoadingRef.current = false;
      setIsManualCalculating(false);
    }
  }, [isCanvasLoading]);

  // Stable function that always runs the latest closure (the canvas in ViewerPanel is memoized)
  const calcRef = useRef();
  calcRef.current = () => (runEngine ? runManualLayout() : broadcast({ type: "recalculate" }));
  const calculateLayout = useCallback(() => calcRef.current(), []);

  // Enclosure was saved: only mark the layout as outdated, the button recalculates
  const onEnclosureChanged = () => {
    setOutdated(true);
    broadcast({ type: "project-changed" });
  };

  const setClimate = (c) => { setClimateState(c); broadcast({ type: "climate", climate: c }); };

  const busy = isCalculating || isManualCalculating;
  useEffect(() => {
    if (runEngine) broadcast({ type: "engine-status", requiredHeight, enclosureTooSmall, busy });
  }, [runEngine, requiredHeight, enclosureTooSmall, busy]);

  const sameIds = (a, b) => a.size === b.size && [...a].every((id) => b.has(id));

  handlersRef.current.onMessage = (m) => {
    switch (m.type) {
      case "project-changed": refetchProject(); break;
      case "recalculate": if (runEngine) runManualLayout(); break;
      case "climate": setClimateState(m.climate); break;
      case "hidden-ids": {
        // Apply only — never re-broadcast a remote state
        const incoming = new Set(m.ids ?? []);
        if (!sameIds(hiddenRef.current, incoming)) {
          hiddenRef.current = incoming;
          setHiddenIds(incoming);
        }
        break;
      }
      case "selected-id":
        selectedRef.current = m.id ?? null;
        setSelectedId(m.id ?? null);
        break;
      case "state-request":
        // Answer a freshly loaded panel with the current state (if any)
        if (hiddenRef.current.size) broadcast({ type: "hidden-ids", ids: [...hiddenRef.current] });
        if (selectedRef.current) broadcast({ type: "selected-id", id: selectedRef.current });
        break;
      case "layout-outdated": setLayoutOutdated(m.value); break;
      case "canvas-capture-request":
        // Only the panel that shows the 2D canvas answers
        if (canvasCaptureRef.current) {
          runLocalCapture().then((result) =>
            broadcast({ type: "canvas-capture-result", requestId: m.requestId, result }));
        }
        break;
      case "canvas-capture-result": {
        const done = pendingCapturesRef.current.get(m.requestId);
        if (done) {
          pendingCapturesRef.current.delete(m.requestId);
          done(m.result);
        }
        break;
      }
      case "engine-status":
        if (!runEngine) { setRequiredHeight(m.requiredHeight); setEnclosureTooSmall(m.enclosureTooSmall); setIsCalculating(m.busy); }
        break;
      default: break;
    }
  };

  const value = {
    projectId, runEngine,
    selectedProject, projectName, handleNameChange, updateProject,
    hasEnclosure, enclosurePlacement, devices, sectionPlacements,
    selectedScenario, handleScenarioSelected,
    orientation, useOptimized, useFlexible,
    enclosureTooSmall, requiredHeight, clearEnclosureBlock, onEnclosureChanged,
    canvasSnapshot, canvasVersion, isCalculating, isManualCalculating, isCanvasLoading, setIsCanvasLoading,
    layoutOutdated,
    calculateLayout,
    handleQuantityChange, handlePlaceholderQuantityChange, handleDeleteDevice,
    handleRailOrientationChange, handleRailOrientationChangeMany, handleReorderDevices,
    handleUnitsPerRailChange,
    climate, setClimate, climateResult,
    sceneHandlers, setSceneHandlers,
    hiddenIds, selectedId, toggleHidden, setHiddenMany, selectId,
    refreshProject,
    reportViewMode,
    getConfiguratorUrl, enclosureConfiguratorUrl, loadConfiguratorLinks,
    registerCanvasCapture, requestCanvasCapture
  };
  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}
